// chartPrepApi.js - client helpers for the Pro Chart Prep feature.
//
// Destination in the deployed repo: src/lib/chartPrepApi.js
//
// This REPLACES the earlier version. New in this revision: the "Chart prep
// actions" section at the bottom (one-click create task / send to Scribe /
// undo, plus reading actioned-state for a note). Everything above that section
// is unchanged.

import { supabase } from "./supabaseClient";
import { chartPrepErrorText, localDateStr as coreLocalDateStr, formatSlotTime as coreFormatSlotTime, flagCounts as coreFlagCounts } from "./chartPrepCore";

// Oct 2026 (chart prep huddle): new columns on the note (version, data_as_of,
// context_status, missing_sections, care_gaps, context_facts, citation_stats,
// last_attempt_failed), review through the audited chart_prep_mark_reviewed
// RPC (reviewed_by is set server-side), and RPC wrappers for staleness,
// provider edits, feedback, version history and the huddle board. RPC errors
// are thrown as readable sentences (chartPrepErrorText).

async function rpc(name, args) {
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw new Error(chartPrepErrorText(error));
  return data;
}

const NOTE_COLUMNS = [
  "id",
  "appointment_id",
  "patient_id",
  "provider_id",
  "appt_date",
  "one_line_summary",
  "chief_reason",
  "key_problems",
  "overdue_measures",
  "recent_changes",
  "suggested_agenda",
  "flags",
  "historical_arc",
  "sdoh_summary",
  "device_notes",
  "vaccine_notes",
  "care_gaps",
  "context_facts",
  "context_status",
  "missing_sections",
  "citation_stats",
  "data_as_of",
  "version",
  "last_attempt_failed",
  "status",
  "error_message",
  "generation_attempts",
  "last_attempt_at",
  "reviewed_by",
  "reviewed_at",
  "provider_note",
  "model",
  "patients:patient_id ( first_name, last_name, mrn, date_of_birth, gender, pronouns )",
  "providers:provider_id ( first_name, last_name )",
  "appointments:appointment_id ( start_slot, appt_type, chief_complaint, confirmation_status, status )",
].join(", ");

// ----------------------------------------------------------------------------
// Internal: call a browser-facing edge function and surface the server's
// error body in the thrown error so the UI can show it.
// ----------------------------------------------------------------------------

async function invoke(name, body) {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    let msg = error.message || "Function invocation failed";
    try {
      if (error.context && typeof error.context.text === "function") {
        const txt = await error.context.text();
        if (txt) msg = msg + " | " + txt.slice(0, 500);
      }
    } catch (_e) {
      // best effort
    }
    throw new Error(msg);
  }
  return data;
}

// ----------------------------------------------------------------------------
// Chart prep notes
// ----------------------------------------------------------------------------

export async function listChartPrepNotesForDate(practiceId, apptDate) {
  const { data, error } = await supabase
    .from("pro_chart_prep_notes")
    .select(NOTE_COLUMNS)
    .eq("practice_id", practiceId)
    .eq("appt_date", apptDate)
    .order("appt_date", { ascending: true });
  if (error) throw new Error(error.message);

  const sorted = (data || []).slice().sort((a, b) => {
    const sa = (a.appointments && a.appointments.start_slot) || 0;
    const sb = (b.appointments && b.appointments.start_slot) || 0;
    return sa - sb;
  });
  return sorted;
}

export async function fetchChartPrepNote(noteId) {
  const { data, error } = await supabase
    .from("pro_chart_prep_notes")
    .select(NOTE_COLUMNS)
    .eq("id", noteId)
    .single();
  if (error) throw new Error(error.message);
  return data;
}

// Review goes through chart_prep_mark_reviewed: the server sets reviewed_by
// to the signed-in user, locks the row, refuses a stale version
// (expectedVersion) and writes the audit row. Direct UPDATEs on the table are
// closed by RLS. Returns { id, status, version, reviewed_at, reviewed_by,
// reviewer_name, provider_note, already_reviewed }.
export async function markChartPrepReviewed(noteId, providerNote, expectedVersion) {
  return rpc("chart_prep_mark_reviewed", {
    p_note_id: noteId,
    p_provider_note: providerNote === undefined ? null : providerNote,
    p_expected_version: Number.isInteger(expectedVersion) ? expectedVersion : null,
  });
}

// { date, today, mode: today | next_business_day | today_fallback, tz, ... }
export async function defaultChartPrepDate() {
  return rpc("chart_prep_default_date", {});
}

// [{ note_id, stale, since, reasons: [{ type, count, latest_at }] }]
export async function fetchChartPrepStaleness(noteIds) {
  const ids = (noteIds || []).filter(Boolean);
  if (ids.length === 0) return [];
  const data = await rpc("chart_prep_staleness", { p_note_ids: ids });
  return Array.isArray(data) ? data : [];
}

// { version, reviewer_name, edits, my_feedback, feedback_totals, versions }
export async function fetchChartPrepOverlay(noteId) {
  return rpc("chart_prep_note_overlay", { p_note_id: noteId });
}

// One archived version (audited server-side as a Read).
export async function fetchChartPrepVersion(noteId, version) {
  return rpc("chart_prep_note_version", { p_note_id: noteId, p_version: version });
}

// Provider edit of one item; blank text reverts to the AI wording.
export async function editChartPrepItem({ noteId, section, itemIndex, text, expectedVersion }) {
  return rpc("chart_prep_edit_item", {
    p_note_id: noteId,
    p_section: section,
    p_item_index: Number.isInteger(itemIndex) ? itemIndex : 0,
    p_text: text || "",
    p_expected_version: Number.isInteger(expectedVersion) ? expectedVersion : null,
  });
}

// Thumbs up / down + optional correction; section null = whole note.
export async function sendChartPrepFeedback({ noteId, section, itemIndex, rating, correction }) {
  return rpc("chart_prep_feedback", {
    p_note_id: noteId,
    p_section: section || null,
    p_item_index: section && Number.isInteger(itemIndex) ? itemIndex : null,
    p_rating: rating || null,
    p_correction: correction || null,
  });
}

// Morning huddle board (audited server-side as one Read).
export async function fetchChartPrepHuddle(apptDate) {
  return rpc("chart_prep_huddle", { p_date: apptDate });
}

export async function setChartPrepHuddleTask({ noteId, taskKey, label, sourceType, status }) {
  return rpc("chart_prep_huddle_task_set", {
    p_note_id: noteId,
    p_task_key: taskKey,
    p_label: label,
    p_source_type: sourceType,
    p_status: status,
  });
}

export async function assignChartPrepHuddleMa({ apptDate, providerId, assigneeId }) {
  return rpc("chart_prep_huddle_assign", {
    p_date: apptDate,
    p_provider_id: providerId,
    p_assignee_id: assigneeId || null,
  });
}

export async function regenerateChartPrep(appointmentId) {
  return invoke("pro-chart-prep-regenerate", { appointment_id: appointmentId });
}

// ----------------------------------------------------------------------------
// Business-day resolution (respects practice_hours + holidays + preferences)
// ----------------------------------------------------------------------------

// Returns a YYYY-MM-DD string representing the next business day for the
// practice. If the RPC errors for any reason, falls back to a plain client-side
// "next weekday" calculation so the caller always gets a date.
export async function nextBusinessDayForPractice(practiceId) {
  try {
    // p_from_date = the LOCAL date. Without it the function uses the
    // database's CURRENT_DATE (UTC), which after 8pm Eastern is already
    // tomorrow and skips a business day.
    const { data, error } = await supabase.rpc("next_business_day_for_practice", {
      p_practice_id: practiceId,
      p_from_date: coreLocalDateStr(),
    });
    if (error) throw error;
    // Only a YYYY-MM-DD string is a date. Anything else (an empty array, an
    // object) used to flow through and print "No appointments scheduled for ."
    if (typeof data === "string" && /^\d{4}-\d{2}-\d{2}$/.test(data)) return data;
  } catch (_e) {
    // fall through to client-side fallback
  }
  return nextWeekdayClient();
}

function nextWeekdayClient() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return coreLocalDateStr(d);
}

// Convenience combo for the Dashboard widget - resolves next business day,
// fetches the notes, and returns { apptDate, notes } in one call.
export async function chartPrepSummaryForNextBusinessDay(practiceId) {
  const apptDate = await nextBusinessDayForPractice(practiceId);
  const notes = await listChartPrepNotesForDate(practiceId, apptDate);
  return { apptDate, notes };
}

// Earliest date on or after afterDate that actually HAS chart prep, optionally
// scoped to one provider.
//
// Deliberately not nextBusinessDayForPractice(): that answers "when is the
// practice next open", which is a different question. A provider with no
// clinic tomorrow but a full Thursday should be offered Thursday, not shown an
// empty Wednesday. Returns null when nothing is scheduled ahead - the caller
// renders a genuine all-clear rather than an empty date.
export async function nextChartPrepDateWithNotes(practiceId, afterDate, providerId) {
  let q = supabase
    .from("pro_chart_prep_notes")
    .select("appt_date")
    .eq("practice_id", practiceId)
    .gt("appt_date", afterDate)
    .order("appt_date", { ascending: true })
    .limit(1);
  if (providerId) q = q.eq("provider_id", providerId);

  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data && data.length > 0) ? data[0].appt_date : null;
}

// Local calendar date as YYYY-MM-DD.
//
// NOT toISOString().slice(0,10) - that converts to UTC first, so any US user
// after 8pm ET gets tomorrow's date and the card silently shows the wrong day
// exactly when someone is prepping for the morning.
export function localDateStr(d) {
  return coreLocalDateStr(d);
}

export function addDaysStr(dateStr, n) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + n);
  return localDateStr(d);
}

// ----------------------------------------------------------------------------
// System alerts
// ----------------------------------------------------------------------------

export async function listOpenSystemAlerts(practiceId) {
  const { data, error } = await supabase
    .from("pro_system_alerts")
    .select("id, alert_type, severity, title, message, ticket_ref, details, created_at, status, email_sent_at")
    .eq("practice_id", practiceId)
    .eq("status", "Open")
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  return data || [];
}

export async function acknowledgeSystemAlert(alertId) {
  const { data, error } = await supabase
    .from("pro_system_alerts")
    .update({
      status: "Acknowledged",
      acknowledged_at: new Date().toISOString(),
    })
    .eq("id", alertId)
    .select("id, status")
    .single();
  if (error) throw new Error(error.message);
  return data;
}

// ----------------------------------------------------------------------------
// Helpers for the view
// ----------------------------------------------------------------------------

export function formatSlotTime(slot) {
  return coreFormatSlotTime(slot);
}

export function patientDisplayName(patient) {
  if (!patient) return "Unknown patient";
  const first = patient.first_name || "";
  const last  = patient.last_name || "";
  const name  = (first + " " + last).trim();
  return name || "Unknown patient";
}

export function patientAge(patient) {
  if (!patient || !patient.date_of_birth) return null;
  const dob = new Date(patient.date_of_birth);
  if (isNaN(dob.getTime())) return null;
  const diffMs = Date.now() - dob.getTime();
  return Math.floor(diffMs / (365.25 * 86400000));
}

export function providerDisplayName(provider) {
  if (!provider) return null;
  const last = provider.last_name || "";
  return last ? ("Dr. " + last) : null;
}

export function flagCounts(flags) {
  return coreFlagCounts(flags);
}

// ----------------------------------------------------------------------------
// Chart prep actions (one-click: create task / send to Scribe)
//
// Writes go through the staff-chart-prep-action edge function; reads come
// directly from pro_chart_prep_actions via its same-practice RLS policy so the
// UI can show actioned state (and undo) per item.
//
// Item identity is (item_kind, item_text) - the server dedupes active actions
// on that, so the view should key its lookup the same way.
// ----------------------------------------------------------------------------

// Routable team roles for the "assign to a team" override. Mirrors the
// server's ROUTABLE_ROLES list. Labels are for display only.
export const CHART_PREP_ROUTE_ROLES = [
  { role: "Provider",                 label: "Provider" },
  { role: "Medical Assistant",        label: "Medical Assistant" },
  { role: "RN",                       label: "RN" },
  { role: "LPN",                      label: "LPN" },
  { role: "Care Manager",             label: "Care Manager" },
  { role: "Supervising Care Manager", label: "Supervising Care Manager" },
  { role: "CHW",                      label: "Community Health Worker" },
  { role: "Health Educator",          label: "Health Educator" },
  { role: "Pharmacist",               label: "Pharmacist" },
  { role: "Front Desk",               label: "Front Desk" },
  { role: "Billing",                  label: "Billing" },
  { role: "Manager",                  label: "Manager" },
];

// Create a task from an agenda item or flag.
// opts: {
//   noteId, itemKind ('agenda'|'flag'), itemIndex, itemText,
//   itemSeverity?,            // flags only
//   assignedRole?,            // route to a team queue; omit for default
//   assignedTo?,              // route to a specific user id; omit for default
//   dueDate?                  // YYYY-MM-DD; omit to default to the appt date
// }
// Omitting both assignedRole and assignedTo defaults the task to the
// appointment's provider (resolved server-side).
export async function createTaskFromChartPrepItem(opts) {
  return invoke("staff-chart-prep-action", {
    chart_prep_note_id: opts.noteId,
    item_kind: opts.itemKind,
    item_index: opts.itemIndex,
    item_text: opts.itemText,
    item_severity: opts.itemSeverity ?? null,
    action_type: "create_task",
    mode: "create",
    assigned_role: opts.assignedRole ?? undefined,
    assigned_to: opts.assignedTo ?? undefined,
    due_date: opts.dueDate ?? undefined,
  });
}

// Stage an agenda item or flag so cmd-scribe-soap-draft injects it into this
// visit's SOAP draft as an advisory pre-visit agenda item.
// opts: { noteId, itemKind, itemIndex, itemText, itemSeverity? }
export async function sendChartPrepItemToScribe(opts) {
  return invoke("staff-chart-prep-action", {
    chart_prep_note_id: opts.noteId,
    item_kind: opts.itemKind,
    item_index: opts.itemIndex,
    item_text: opts.itemText,
    item_severity: opts.itemSeverity ?? null,
    action_type: "send_to_scribe",
    mode: "create",
  });
}

// Undo a previously taken action (matches on note + action_type + kind + text).
// For send_to_scribe this unstages it; for create_task it clears the marker but
// leaves the task in place.
// opts: { noteId, itemKind, itemText, actionType ('create_task'|'send_to_scribe') }
export async function undoChartPrepAction(opts) {
  return invoke("staff-chart-prep-action", {
    chart_prep_note_id: opts.noteId,
    item_kind: opts.itemKind,
    item_text: opts.itemText,
    action_type: opts.actionType,
    mode: "undo",
  });
}

// Read active actions for a note so the view can render actioned state per item.
export async function listChartPrepActionsForNote(noteId) {
  const { data, error } = await supabase
    .from("pro_chart_prep_actions")
    .select("id, item_kind, item_index, item_text, action_type, status, result_task_id, scribe_consumed_at, created_at")
    .eq("chart_prep_note_id", noteId)
    .eq("status", "active")
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return data || [];
}

// Build a quick lookup so the view can ask "is this item already actioned?".
// Key is `${item_kind}::${item_text}::${action_type}` -> the action row.
export function indexChartPrepActions(actions) {
  const map = {};
  for (const a of actions || []) {
    map[a.item_kind + "::" + a.item_text + "::" + a.action_type] = a;
  }
  return map;
}

// Cross-link helpers: chart prep <-> clinical note, joined on appointment_id.
export async function findEncounterIdForAppointment(appointmentId) {
  if (!appointmentId) return null;
  const { data, error } = await supabase
    .from("encounters")
    .select("id")
    .eq("appointment_id", appointmentId)
    .order("created_at", { ascending: false })
    .limit(1);
  if (error) { console.warn("[chartPrepApi] findEncounterIdForAppointment", error.message); return null; }
  return data && data.length ? data[0].id : null;
}

export async function findChartPrepNoteForAppointment(appointmentId) {
  if (!appointmentId) return null;
  const { data, error } = await supabase
    .from("pro_chart_prep_notes")
    .select("id, appointments(appt_date)")
    .eq("appointment_id", appointmentId)
    .limit(1);
  if (error) { console.warn("[chartPrepApi] findChartPrepNoteForAppointment", error.message); return null; }
  if (!data || !data.length) return null;
  return { id: data[0].id, apptDate: data[0].appointments && data[0].appointments.appt_date };
}
